// import { useContext, useState, useCallback } from "react";
// import AuthContext from "../store/authContext";
// import axios from "axios";

// function useFetch() {
//     const authCtx = useContext(AuthContext);
//     const BASE_URL = process.env.REACT_APP_API_URL;

//     // Initial states
//     const [state, setState] = useState({
//         data: null,
//         isLoading: false,
//         error: null
//     });

//     // Create headers with authentication when token exists
//     const getHeaders = useCallback((contentType = "application/json") => {
//         const headers = contentType === "multipart/form-data"
//             ? JSON.parse(process.env.REACT_APP_API_HEADERS_FORM)
//             : JSON.parse(process.env.REACT_APP_API_HEADERS);

//         if (authCtx.token) {
//             headers["Authorization"] = `Bearer ${authCtx.token}`;
//         }

//         return headers;
//     }, [authCtx.token]);

//     // Create axios instance with default config
//     const createAxiosInstance = useCallback((baseUrl = BASE_URL) => {
//         const instance = axios.create({
//             baseURL: baseUrl,
//         });

//         // Add response interceptor for consistent error handling
//         instance.interceptors.response.use(
//             response => response.data,
//             error => {
//                 const errorMessage =
//                     error?.response?.data?.error ||
//                     error?.response?.data?.message ||
//                     error?.message ||
//                     "An error occurred";

//                 return Promise.reject(errorMessage);
//             }
//         );

//         return instance;
//     }, [BASE_URL]);

//     // Generic request handler
//     const request = useCallback(async (method, url, data = null, options = {}) => {
//         const {
//             baseUrl = BASE_URL,
//             contentType = "application/json",
//             params = null,
//             useFormData = false
//         } = options;

//         setState(prev => ({ ...prev, isLoading: true, error: null }));

//         try {
//             const axiosInstance = createAxiosInstance(baseUrl);
//             const headers = getHeaders(contentType);

//             let requestData = data;

//             // Convert to FormData if needed
//             if (useFormData && data) {
//                 const formData = new FormData();
//                 Object.entries(data).forEach(([key, value]) => {
//                     formData.append(key, value);
//                 });
//                 requestData = formData;
//             }

//             const response = await axiosInstance({
//                 method,
//                 url,
//                 headers,
//                 data: requestData,
//                 params
//             });

//             // Handle API response format variations
//             const responseData = response;

//             setState({
//                 data: responseData,
//                 isLoading: false,
//                 error: null
//             });

//             return responseData;
//         } catch (error) {
//             setState(prev => ({
//                 ...prev,
//                 isLoading: false,
//                 error
//             }));

//             throw error;
//         }
//     }, [BASE_URL, createAxiosInstance, getHeaders]);

//     // Specific HTTP method implementations
//     const get = useCallback((url, params = null, options = {}) => {
//         return request("get", url, null, { ...options, params });
//     }, [request]);

//     const post = useCallback((url, data = null, options = {}) => {
//         return request("post", url, data, options);
//     }, [request]);

//     const postForm = useCallback((url, data = null, options = {}) => {
//         return request("post", url, data, {
//             ...options,
//             contentType: "multipart/form-data",
//             useFormData: true
//         });
//     }, [request]);

//     const put = useCallback((url, data = null, options = {}) => {
//         return request("put", url, data, options);
//     }, [request]);

//     const patch = useCallback((url, data = null, options = {}) => {
//         return request("patch", url, data, options);
//     }, [request]);

//     const remove = useCallback((url, data = null, options = {}) => {
//         return request("delete", url, data, options);
//     }, [request]);

//     const login = useCallback((url, credentials, options = {}) => {
//         return request("post", url, credentials, {
//             ...options,
//             contentType: "multipart/form-data",
//             useFormData: true
//         });
//     }, [request]);

//     return {
//         ...state, // Includes `data`, `isLoading`, and `error`
//         get,
//         post,
//         postForm,
//         put,
//         patch,
//         delete: remove,
//         login,
//         request
//     };
// }

// export default useFetch;


import { useContext, useState, useMemo, useCallback } from "react";
import AuthContext from "../store/authContext";
import axios from "axios";

function useFetch() {
    const authCtx = useContext(AuthContext);
    const BASE_URL = process.env.REACT_APP_API_URL;

    // Parse static headers once
    const apiHeaders = useMemo(() => JSON.parse(process.env.REACT_APP_API_HEADERS), []);
    const apiHeadersForm = useMemo(() => JSON.parse(process.env.REACT_APP_API_HEADERS_FORM), []);

    // Global axios instance with interceptor applied once
    const axiosInstance = useMemo(() => {
        const instance = axios.create({ baseURL: BASE_URL });
        instance.interceptors.response.use(
            response => response.data,
            error => {
                // You can add status etc. here if needed
                const errorMessage =
                    error?.response?.data?.error ||
                    error?.response?.data?.message ||
                    error?.message ||
                    "An error occurred";
                return Promise.reject({ message: errorMessage, status: error?.response?.status });
            }
        );
        return instance;
    }, [BASE_URL]);

    // State for request status
    const [state, setState] = useState({
        data: null,
        isLoading: false,
        error: null,
    });

    // Build headers for each request
    const getHeaders = useCallback((contentType = "application/json") => {
        let headers = contentType === "multipart/form-data"
            ? { ...apiHeadersForm }
            : { ...apiHeaders };

        if (authCtx.token) {
            headers["Authorization"] = `Bearer ${authCtx.token}`;
        }

        // Only set Content-Type if not multipart
        if (contentType !== "multipart/form-data") {
            headers["Content-Type"] = contentType;
        }

        return headers;
    }, [authCtx.token, apiHeaders, apiHeadersForm]);

    // Generic request handler
    const request = useCallback(async (method, url, data = null, options = {}) => {
        const {
            contentType = "application/json",
            params = null,
            useFormData = false,
            headers: customHeaders = {},
        } = options;

        setState(prev => ({ ...prev, isLoading: true, error: null }));

        try {
            let requestData = data;
            let headers = { ...getHeaders(contentType), ...customHeaders };

            // If FormData, let axios set Content-Type with proper boundaries
            if (useFormData && data) {
                const formData = new FormData();
                Object.entries(data).forEach(([key, value]) => {
                    formData.append(key, value);
                });
                requestData = formData;
                // Remove Content-Type so axios sets it correctly
                delete headers["Content-Type"];
            }

            const response = await axiosInstance({
                method,
                url,
                headers,
                data: requestData,
                params
            });

            setState({
                data: response,
                isLoading: false,
                error: null,
            });

            return response;
        } catch (error) {
            setState(prev => ({
                ...prev,
                isLoading: false,
                error,
            }));
            throw error;
        }
    }, [axiosInstance, getHeaders]);

    // HTTP methods
    const get = useCallback((url, params = null, options = {}) => {
        return request("get", url, null, { ...options, params });
    }, [request]);

    const post = useCallback((url, data = null, options = {}) => {
        return request("post", url, data, options);
    }, [request]);

    const postForm = useCallback((url, data = null, options = {}) => {
        return request("post", url, data, {
            ...options,
            contentType: "multipart/form-data",
            useFormData: true
        });
    }, [request]);

    const put = useCallback((url, data = null, options = {}) => {
        return request("put", url, data, options);
    }, [request]);

    const patch = useCallback((url, data = null, options = {}) => {
        return request("patch", url, data, options);
    }, [request]);

    const remove = useCallback((url, data = null, options = {}) => {
        return request("delete", url, data, options);
    }, [request]);

    const login = useCallback((url, credentials, options = {}) => {
        return request("post", url, credentials, {
            ...options,
            contentType: "multipart/form-data",
            useFormData: true
        });
    }, [request]);

    return {
        ...state,
        get,
        post,
        postForm,
        put,
        patch,
        delete: remove,
        login,
        request,
    };
}

export default useFetch;